import React, { useMemo, type ComponentType } from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import { IssueBreakdownButton } from '@atlassian/jira-ai-work-breakdown/src/ui/issue-breakdown-button/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIsAiEnabledForIssue } from '@atlassian/jira-issue-context-service/src/main.tsx';
import type { ChildIssue } from '@atlassian/jira-issue-shared-types/src/common/types/children-issues-type.tsx';
import type { ChildIssuesLimitStatus } from '@atlassian/jira-issue-view-common-constants/src/child-issues.tsx';
import withContainerWidth from '@atlassian/jira-issue-view-common-utils/src/with-container-width/index.tsx';
import {
	SectionHeading,
	SectionHeadingIcons,
} from '@atlassian/jira-issue-view-common/src/component/section-heading/section-heading-view.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { RefObject } from '@atlassian/jira-shared-types/src/general.tsx';
import { hasExceededIssuesLimitInitialLoad } from '../../common/utils/has-exceeded-issues-limit/index.tsx';
import { useIsAiEnabled } from '../utils.tsx';
import type { AddChildEventHandler } from './add-child/types.tsx';
import HeaderSectionHeadingIcons from './section-heading-icons/index.tsx';

// This number was calculated by hand to see when buttons started to overflow
const SINGLE_COLUMN_MAX_WIDTH = 440;

type ConnectProps = {
	isSortingAvailable: boolean;
	Title: ComponentType<{
		hasMultipleChildPanels?: boolean;
		childIssuesLimitStatus: ChildIssuesLimitStatus;
	}>;
	Meatballs: ComponentType<{
		childIssuesLimitStatus: ChildIssuesLimitStatus;
		isSortingAvailable?: boolean;
		// TODO: Make required on hide-rank-option-when-unavailable cleanup
		isRankingEnabled?: boolean;
	}>;
	AddChild: ComponentType<{
		onClick?: AddChildEventHandler;
	}>;
	SortByMenu: ComponentType<{
		childIssuesLimitStatus: ChildIssuesLimitStatus;
	}>;
	allIssues: ChildIssue[];
	supportsIssueCreation: boolean | undefined;
};

type ParentProps = {
	sourceName?: string;
	childIssuesLimitStatus: ChildIssuesLimitStatus;
	onAddChildClick: AddChildEventHandler | undefined;
	issueHierarchyLevel: number | undefined;
	containerWidth: number;
	isHideDoneFilterEnabled: boolean | undefined | null;
	userPreferencesConnectionId: string | undefined;
	// TODO: Make required on hide-rank-option-when-unavailable cleanup
	isRankingEnabled?: boolean;
	columnPickerRef?: RefObject<HTMLDivElement>;
	hasMultipleChildPanels?: boolean;
};

export type Props = ConnectProps & ParentProps;

function Heading(props: Props) {
	const {
		Title,
		Meatballs,
		AddChild,
		SortByMenu,
		supportsIssueCreation,
		allIssues,
		onAddChildClick,
		sourceName,
		isSortingAvailable,
		childIssuesLimitStatus,
		issueHierarchyLevel,
		containerWidth,
		isHideDoneFilterEnabled,
		userPreferencesConnectionId,
		isRankingEnabled,
		columnPickerRef,
		hasMultipleChildPanels,
	} = props;

	const isMeatballMenuEnabled =
		hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus) || allIssues.length > 0;

	const isAiEnabled = useIsAiEnabled();

	const isAiOptInEnabled = useIsAiEnabledForIssue();

	const isAnonymousUser = useIsAnonymous();

	const renderIssueBreakdownButton = useMemo(() => {
		return (
			<SpotlightManager>
				<SpotlightTarget name="suggestIssuesSpotlight">
					<IssueBreakdownButton
						issueHierarchyLevel={issueHierarchyLevel}
						isCompact={
							(!!containerWidth && containerWidth <= SINGLE_COLUMN_MAX_WIDTH) ||
							expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false)
						}
					/>
				</SpotlightTarget>

				<EngagementSpotlight engagementId="suggestIssuesSpotlight" />
			</SpotlightManager>
		);
	}, [containerWidth, issueHierarchyLevel]);

	const oldSectionHeadingIcons = () => {
		return (
			<SectionHeadingIcons>
				{isSortingAvailable && <SortByMenu childIssuesLimitStatus={childIssuesLimitStatus} />}
				{isMeatballMenuEnabled ? (
					<Meatballs childIssuesLimitStatus={childIssuesLimitStatus} />
				) : null}
				{supportsIssueCreation ? <AddChild onClick={onAddChildClick} /> : null}

				{!isAnonymousUser &&
				supportsIssueCreation &&
				isAiEnabled &&
				isAiOptInEnabled &&
				!expVal('proactive_aiwb_2', 'enabled', false) ? (
					<Box xcss={oldIssueBreakdownButtonStyles}>{renderIssueBreakdownButton}</Box>
				) : null}
			</SectionHeadingIcons>
		);
	};

	const ConfigurableChildIssueHeading = () => {
		return (
			<Box
				xcss={
					fg('magma-3191-render-chin-skeleton-in-ssr')
						? [marginBottomStyles, minHeightStyles]
						: marginBottomStyles
				}
			>
				<SectionHeading>
					{}
					<label htmlFor={sourceName}>
						<Title
							hasMultipleChildPanels={hasMultipleChildPanels}
							childIssuesLimitStatus={childIssuesLimitStatus}
						/>
					</label>
					{hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus) &&
					fg('jiv-chin-child-issue-limit-panel-header-enablement') ? null : (
						<HeaderSectionHeadingIcons
							columnPickerRef={columnPickerRef}
							onAddChildClick={onAddChildClick}
							childIssuesLimitStatus={childIssuesLimitStatus}
							issueHierarchyLevel={issueHierarchyLevel}
							isRankingEnabled={
								fg('hide-rank-option-when-unavailable') ? isRankingEnabled : undefined
							}
							userPreferencesConnectionId={userPreferencesConnectionId}
							isHideDoneFilterEnabled={isHideDoneFilterEnabled}
						/>
					)}
				</SectionHeading>
			</Box>
		);
	};

	const OldChildIssueHeading = () => {
		return (
			<SectionHeading>
				{}
				<label htmlFor={sourceName}>
					<Title
						hasMultipleChildPanels={hasMultipleChildPanels}
						childIssuesLimitStatus={childIssuesLimitStatus}
					/>
				</label>
				{oldSectionHeadingIcons()}
			</SectionHeading>
		);
	};

	return expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false)
		? ConfigurableChildIssueHeading()
		: OldChildIssueHeading();
}

export default withContainerWidth(Heading);

const oldIssueBreakdownButtonStyles = xcss({
	marginLeft: 'space.075',
});

const marginBottomStyles = xcss({
	marginBottom: 'space.100',
});

// This minHeight is used to ensure that the heading does not jump when the meatball menu appears
const minHeightStyles = xcss({
	minHeight: '24px',
});

Heading.defaultProps = {
	isSortingAvailable: false,
	supportsIssueCreation: false,
	allIssues: [],
};
